import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const ProductSalesAnalytics = () => {
    const { authFetch, selectedDepo } = useContext(AppContext);
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of current month
        end_date: new Date().toISOString().split('T')[0],
        category_id: 'All',
        depo_id: selectedDepo
    });

    const [categories, setCategories] = useState([]);
    const [data, setData] = useState({ profitable: [], worst: [], trend: [] });

    useEffect(() => {
        authFetch('/api/categories').then(r => r.json()).then(d => setCategories(Array.isArray(d) ? d : []));
    }, [authFetch]);

    useEffect(() => {
        fetchAnalytics();
    }, [filters, authFetch, selectedDepo]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                start_date: filters.start_date,
                end_date: filters.end_date,
                category_id: filters.category_id,
                depo_id: selectedDepo
            });
            const res = await authFetch(`/api/product_analytics?${params.toString()}`);
            const resData = await res.json();
            if (res.ok) {
                setData({
                    profitable: Array.isArray(resData.profitable) ? resData.profitable : [],
                    worst: Array.isArray(resData.worst) ? resData.worst : [],
                    trend: Array.isArray(resData.trend) ? resData.trend : []
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CFA' }).format(val || 0);

    // Chart configs
    const trendData = {
        labels: data.trend.map(d => d.sale_date),
        datasets: [
            {
                label: t('Sales Revenue'),
                data: data.trend.map(d => parseFloat(d.total_revenue)),
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.5)',
                yAxisID: 'y',
                tension: 0.3
            },
            {
                label: t('Quantity Sold'),
                data: data.trend.map(d => parseInt(d.total_qty)),
                borderColor: 'rgb(16, 185, 129)',
                backgroundColor: 'rgba(16, 185, 129, 0.5)',
                yAxisID: 'y1',
                tension: 0.3
            }
        ]
    };

    const trendOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
            y: { type: 'linear', display: true, position: 'left' },
            y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false } }
        }
    };

    return (
        <div className="space-y-6">
            <header>
                <h2 className="text-3xl font-bold text-gray-800">{t('Product Sales Analytics')}</h2>
                <p className="text-gray-500 mt-1">{t('Deep dive into what products are selling.')}</p>
            </header>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-wrap gap-4 items-end">
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Start Date')}</label>
                    <input type="date" value={filters.start_date} onChange={e => handleFilterChange('start_date', e.target.value)} className="border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('End Date')}</label>
                    <input type="date" value={filters.end_date} onChange={e => handleFilterChange('end_date', e.target.value)} className="border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Category')}</label>
                    <select value={filters.category_id} onChange={e => handleFilterChange('category_id', e.target.value)} className="border p-2 rounded text-sm min-w-[200px]">
                        <option value="All">{t('All Categories')}</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64 text-indigo-600">
                    <svg className="animate-spin h-10 w-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Sales Trend Line Chart */}
                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">{t('Sales Trend Over Time')}</h3>
                        <div className="h-80">
                            {data.trend.length > 0 ? (
                                <Line data={trendData} options={trendOptions} />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400">{t('No data available for this period.')}</div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top 10 Most Profitable */}
                        <div className="bg-white p-5 rounded-xl border border-emerald-200 shadow-sm overflow-hidden">
                            <h3 className="text-lg font-bold text-emerald-800 mb-4 flex items-center gap-2">
                                <span>⭐</span> {t('Top 10 Most Profitable Products')}
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-emerald-50 text-emerald-900 border-b border-emerald-100">
                                        <tr>
                                            <th className="p-3">{t('Product')}</th>
                                            <th className="p-3 text-right">{t('Qty Sold')}</th>
                                            <th className="p-3 text-right">{t('Revenue')}</th>
                                            <th className="p-3 text-right">{t('Profit')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-emerald-50">
                                        {data.profitable.map((p, i) => (
                                            <tr key={i} className="hover:bg-emerald-50/50">
                                                <td className="p-3 font-semibold text-gray-800">{p.name}</td>
                                                <td className="p-3 text-right">{p.qty_sold}</td>
                                                <td className="p-3 text-right">{formatCurrency(p.revenue)}</td>
                                                <td className="p-3 text-right font-bold text-emerald-600">{formatCurrency(p.profit)}</td>
                                            </tr>
                                        ))}
                                        {data.profitable.length === 0 && (
                                            <tr><td colSpan="4" className="text-center p-4 text-gray-500">{t('No data')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Top 10 Worst Selling */}
                        <div className="bg-white p-5 rounded-xl border border-red-200 shadow-sm overflow-hidden">
                            <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                                <span>⚠️</span> {t('Top 10 Worst Selling Products')}
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-red-50 text-red-900 border-b border-red-100">
                                        <tr>
                                            <th className="p-3">{t('Product')}</th>
                                            <th className="p-3 text-right">{t('Qty Sold')}</th>
                                            <th className="p-3 text-right">{t('Revenue')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-red-50">
                                        {data.worst.map((p, i) => (
                                            <tr key={i} className="hover:bg-red-50/50">
                                                <td className="p-3 font-semibold text-gray-800">{p.name}</td>
                                                <td className="p-3 text-right font-bold text-red-600">{p.qty_sold}</td>
                                                <td className="p-3 text-right text-gray-600">{formatCurrency(p.revenue)}</td>
                                            </tr>
                                        ))}
                                        {data.worst.length === 0 && (
                                            <tr><td colSpan="3" className="text-center p-4 text-gray-500">{t('No data')}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductSalesAnalytics;
